import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token refresh interval - refresh every 30 minutes for seamless experience
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("crpl_token"));
  const [loading, setLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Silent token refresh
  const refreshToken = useCallback(async () => {
    const savedToken = localStorage.getItem("crpl_token");
    if (!savedToken) return false;
    
    try {
      const response = await axios.post(`${API}/auth/refresh`, {}, {
        headers: { Authorization: `Bearer ${savedToken}` },
      });
      const { token: newToken } = response.data;
      if (newToken) {
        localStorage.setItem("crpl_token", newToken);
        setToken(newToken);
        console.log("Token refreshed successfully");
        return true;
      }
    } catch (error) {
      console.log("Token refresh failed:", error.message);
      return false;
    }
    return false;
  }, []);

  // Track user activity and refresh token proactively
  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();
      // If more than 5 minutes since last activity, refresh token
      if (now - lastActivity > 5 * 60 * 1000) {
        refreshToken();
      }
      setLastActivity(now);
    };

    // Track user interactions
    window.addEventListener('click', handleActivity);
    window.addEventListener('keypress', handleActivity);
    window.addEventListener('scroll', handleActivity);
    window.addEventListener('mousemove', handleActivity);

    return () => {
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keypress', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
    };
  }, [lastActivity, refreshToken]);

  // Initialize auth and set up refresh interval
  useEffect(() => {
    let cancelled = false;
    const initAuth = async () => {
      const savedToken = localStorage.getItem("crpl_token");
      if (!savedToken) {
        setLoading(false);
        return;
      }

      // Step 1: validate current token
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${savedToken}` },
        });
        if (!cancelled) {
          setUser(response.data);
          setToken(savedToken);
        }
      } catch (error) {
        // Step 2: if 401, try refresh before giving up
        if (error.response?.status === 401) {
          try {
            const refreshRes = await axios.post(`${API}/auth/refresh`, {}, {
              headers: { Authorization: `Bearer ${savedToken}` },
            });
            const newToken = refreshRes.data?.token;
            if (newToken && !cancelled) {
              localStorage.setItem("crpl_token", newToken);
              setToken(newToken);
              // Retry /auth/me with new token
              const meRes = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${newToken}` },
              });
              if (!cancelled) setUser(meRes.data);
            }
          } catch {
            // Both failed — clear auth
            if (!cancelled) {
              localStorage.removeItem("crpl_token");
              setToken(null);
              setUser(null);
            }
          }
        }
      }
      if (!cancelled) setLoading(false);
    };
    initAuth();

    // Background token refresh every 30 minutes
    const refreshInterval = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
    };
  }, [refreshToken]);

  // Axios interceptor for automatic retry on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const url = originalRequest?.url || '';
        
        // Skip interceptor for auth endpoints to prevent loops
        if (url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/me')) {
          return Promise.reject(error);
        }
        
        // If 401 and not already retried, try to refresh token
        if (error.response?.status === 401 && !originalRequest._retry && token) {
          originalRequest._retry = true;
          
          try {
            const response = await axios.post(`${API}/auth/refresh`, {}, {
              headers: { Authorization: `Bearer ${token}` },
            });
            const { token: newToken } = response.data;
            
            if (newToken) {
              localStorage.setItem("crpl_token", newToken);
              setToken(newToken);
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            // Refresh failed — clear state, React router will redirect via ProtectedRoute
            localStorage.removeItem("crpl_token");
            setToken(null);
            setUser(null);
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  const login = async (username, password) => {
    const response = await axios.post(`${API}/auth/login`, { username, password });
    const { token: newToken } = response.data;
    localStorage.setItem("crpl_token", newToken);
    setToken(newToken);
    // Fetch the fully-resolved user object (includes permissions + category_id)
    // The /auth/login response only returns basic fields, so we hydrate here.
    let fullUser = response.data.user;
    try {
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${newToken}` },
      });
      fullUser = meRes.data;
    } catch {
      // Fall back to the login response if /me fails — user can still navigate.
    }
    setUser(fullUser);
    return fullUser;
  };

  const logout = () => {
    localStorage.removeItem("crpl_token");
    setToken(null);
    setUser(null);
  };

  const requestOTP = async (username) => {
    const response = await axios.post(`${API}/auth/request-otp`, { username });
    return response.data;
  };

  const verifyOTP = async (username, otp) => {
    const response = await axios.post(`${API}/auth/verify-otp`, { username, otp });
    return response.data;
  };

  const resetPassword = async (username, newPassword, otp) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      username,
      new_password: newPassword,
      otp,
    });
    return response.data;
  };

  const changePassword = async (oldPassword, newPassword) => {
    const response = await axios.post(
      `${API}/auth/change-password`,
      { old_password: oldPassword, new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        login,
        logout,
        requestOTP,
        verifyOTP,
        resetPassword,
        changePassword,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
